import { Error, LessonPageSkeleton, PageContainer } from '@/components'
import { LessonPage, SlideDrawer } from '@/components/lesson'
import { SlidesRefsProvider, useAuthHeaders } from '@/providers'
import { useBuildColourMap, useChatContext, useSlideDrawerHooks } from '@/hooks'

import type { Language } from '@/types'
import { createFileRoute } from '@tanstack/react-router'
import { getLessonQueryOptions } from '@/requestObjects/getLessonQueryOptions'
import { getUserLessonQueryOptions } from '@/requestObjects'
import { useQueries } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const lessonSearchSchema = z.object({
  section: z.number().optional(),
  tab: z
    .enum(['mind-map', 'slides', 'presentation', 'review'])
    .optional()
    .default('mind-map'),
})

export type LessonSearch = z.infer<typeof lessonSearchSchema>

export const Route = createFileRoute(
  '/_authRoutes/_appWrapper/lesson/$lessonId',
)({
  component: LessonPlanById,
  validateSearch: (search) => lessonSearchSchema.parse(search),
})

function LessonPlanById() {
  const { t, i18n } = useTranslation()

  // tanstack router params and search params
  const { lessonId } = Route.useParams()

  const headers = useAuthHeaders()
  // function to start the presentation

  // query to create/get the user lesson plan
  const [
    {
      data: userLessonPlan,
      isLoading: userLessonPlanLoading,
      error: userLessonPlanError,
    },
    { data: lesson, isLoading: lessonLoading, error: lessonError },
  ] = useQueries({
    queries: [
      {
        ...getUserLessonQueryOptions({
          lessonId,
          headers,
          language: i18n.language as Language,
        }),
      },
      { ...getLessonQueryOptions({ lessonId, headers }) },
    ],
  })

  useChatContext({ lessonId, lessonMetadata: lesson })

  // Slide Drawer
  const {
    slideDrawerWidth,
    setIsDragging,
    mouseMoveHandler,
    touchMoveHandler,
    dragStopHandler,
  } = useSlideDrawerHooks({ slideDrawerStorageKey: 'lesson_slideDrawerWidth' })

  const isLoading = userLessonPlanLoading || lessonLoading
  const error = userLessonPlanError || lessonError
  const hasData = userLessonPlan && lesson

  const slideImages = lesson?.s3_uris || []

  const slideColorMap = useBuildColourMap({ userLessonPlan, slideImages })

  return (
    <SlidesRefsProvider>
      <div className="min-h-screen">
        <title>
          {t('lesson.planner.title')} {lessonId}
        </title>
        <div
          className="flex flex-row relative"
          onMouseMove={mouseMoveHandler}
          onMouseUp={dragStopHandler}
          onMouseLeave={dragStopHandler}
          onTouchMove={touchMoveHandler}
          onTouchEnd={dragStopHandler}
          onTouchCancel={dragStopHandler}
          style={{ touchAction: 'pan-y' }}
        >
          <SlideDrawer
            slideImages={slideImages}
            slideDrawerWidth={slideDrawerWidth}
            onMouseDown={() => setIsDragging(true)}
            onTouchStart={() => setIsDragging(true)}
            colourLookup={slideColorMap}
          />
          {/* Lesson Summary */}
          <PageContainer className="flex-1 pb-16">
            {isLoading && <LessonPageSkeleton />}
            {error && (
              <Error>
                <div>
                  {t('common.errorWithMessage', { message: error.message })}
                </div>
              </Error>
            )}
            {hasData && (
              <LessonPage
                userLessonPlan={userLessonPlan}
                slideImages={slideImages}
                lessonId={lessonId}
                lessonMetadata={lesson}
                slideColorMap={slideColorMap}
              />
            )}
          </PageContainer>
        </div>
      </div>
    </SlidesRefsProvider>
  )
}
